import './Button.scss'

import {ButtonBase} from '@material-ui/core'
import {makeStyles} from '@material-ui/core/styles'
import React, {HTMLProps, useState} from 'react'
import {NavLink} from 'react-router-dom'
import {ArrowIcon, ButtonEditIcon, ButtonShareIcon} from 'styles/inline-icons'
import {Link} from 'util/link'
import {Mod, style} from 'util/style'

const styles = style('button', ['text', 'icon', 'ripple'])

type ButtonEl = HTMLProps<HTMLButtonElement>

type ButtonProps =
	| ({as: 'button'} & ButtonEl)
	| NavLink['props']
	| HTMLProps<HTMLAnchorElement>

export type ButtonIcons = 'arrow' | 'share' | 'edit'

export type ButtonData = ButtonProps & {
	loading?: boolean
	mod?: Mod<ButtonMods>
	className?: string
	disabled?: boolean
	withIcon?: ButtonIcons
	classPrefix?: string
	campaignButton?: boolean
}

const useRippleStyles = (bgcolor) =>
	makeStyles((theme) => ({
		root: {
			zIndex: -1
		},
		rippleVisible: {
			opacity: 1,
			animation: `$enter ${550}ms ${theme.transitions.easing.easeInOut}`
		},
		child: {
			backgroundColor: bgcolor || '#269c11'
		},
		'@keyframes enter': {
			'0%': {
				transform: 'scale(0)',
				opacity: 0
			},
			'100%': {
				transform: 'scale(1)',
				opacity: 1
			}
		}
	}))

const lightRipple = useRippleStyles('#EFF9ED')

const greyRipple = useRippleStyles('#d5dde0')

const redRipple = useRippleStyles('#ff585f')

const darkRipple = useRippleStyles('#269c11')

export type ButtonMods =
	| 'whitebg'
	| 'greybg'
	| 'redbg'
	| 'outline'
	| 'rounded'
	| 'boldtext'
	| 'gradient'
	| 'minwidth'
	| 'transparent'
	| 'lightgreybg'
	| 'noicon'
	| 'noborder'
	| ButtonIcons

export const ButtonCampaign: React.FC<ButtonData> = (props) => (
	<Button campaignButton {...props} withIcon={'arrow'} />
)

export const Button: React.FC<ButtonData> = ({loading, withIcon, ...props}) => {
	const [isFocused, setFocused] = useState(false)
	const {
		mod,
		disabled,
		children,
		type,
		classPrefix,
		campaignButton = false,
		...rest
	} = props
	const needsLightRipple = hasMod(mod, ['whitebg', 'greybg'])
	const needsGreyRiple = hasMod(mod, ['lightgreybg'])
	const needsRedRiple = hasMod(mod, ['redbg'])

	const ripple = needsLightRipple
		? lightRipple({})
		: needsGreyRiple
		? greyRipple({})
		: needsRedRiple
		? redRipple({})
		: darkRipple({})

	return (
		<div
			className={styles
				.mod(props.mod)
				.is({loading, disabled, focused: isFocused})
				.with(classPrefix && classPrefix)
				.with(campaignButton && 'mod-campaign')}
		>
			<ButtonBase
				component={Link}
				{...(rest as any)}
				type={type}
				TouchRippleProps={{classes: ripple}}
				onFocus={() => setFocused(true)}
				onBlur={() => setFocused(false)}
				classes={{
					root: classPrefix && `${classPrefix}-root`
				}}
				focusRipple
				disabled={disabled}
			>
				<span className={styles('loader')} />
				<div
					className={styles('container')
						.mod(props.mod)
						.with(classPrefix && `${classPrefix}-container`)}
				>
					{children}
					{!hasMod(mod, 'noicon') && <ButtonIcon icon={withIcon} />}
				</div>
			</ButtonBase>
		</div>
	)
}
const getIcon = (icon) => {
	switch (icon) {
		case 'arrow':
			return <ArrowIcon />
		case 'share':
			return <ButtonShareIcon />
		case 'edit':
			return <ButtonEditIcon />
	}
}
export const ButtonText: React.FC = (props) => (
	<span {...props} className={styles.text.mergeProps(props)} />
)

const hasMod = (mods: Mod<ButtonMods>, mod: ButtonMods | Array<ButtonMods>) => {
	if (Array.isArray(mod)) {
		return mod.find((m) => {
			return checkIfHasMod(mods, m)
		})
	}
	return checkIfHasMod(mods, mod)
}

const checkIfHasMod = (mods: Mod<ButtonMods>, mod: ButtonMods) => {
	if (!mods) return false

	// mod can be object
	if (mods.hasOwnProperty(mod)) return true

	// mod can be string
	if (mods === mod) return true

	// mod can be array
	if (Array.isArray(mods) && mods.includes(mod)) return true

	return false
}

export const ButtonIcon: React.FC<{icon: string}> = ({icon, ...props}) => {
	if (!icon) return null
	return (
		<span {...props} className={styles.icon.mergeProps(props)}>
			{getIcon(icon)}
		</span>
	)
}

export const ButtonPrimary: React.FC<ButtonData> = ({children, ...props}) => (
	<ButtonCampaign
		{...props}
		className={styles.mod('boldtext').mergeProps(props)}
	>
		<ButtonText>{children}</ButtonText>
	</ButtonCampaign>
)

export const ButtonSecondary: React.FC<ButtonData> = ({children, ...props}) => (
	<ButtonCampaign
		{...props}
		mod={{outline: true, whitebg: true, boldtext: true}}
	>
		<ButtonText>{children}</ButtonText>
	</ButtonCampaign>
)
export const ButtonTertiary: React.FC<ButtonData> = ({children, ...props}) => {
	return (
		<ButtonCampaign {...props} mod={['outline', 'boldtext']}>
			<ButtonText>{children}</ButtonText>
		</ButtonCampaign>
	)
}

export const ButtonOptional: React.FC<ButtonData> = ({children, ...props}) => (
	<ButtonCampaign {...props} mod="whitebg">
		<ButtonText>{children}</ButtonText>
	</ButtonCampaign>
)
export const ButtonRounded: React.FC<ButtonData> = ({children, ...props}) => (
	<ButtonCampaign {...props} mod="rounded">
		<ButtonText>{children}</ButtonText>
	</ButtonCampaign>
)
export const ButtonShare: React.FC<ButtonData> = ({
	children,
	mod,
	...props
}) => {
	return (
		<ButtonCampaign
			mod={{rounded: true, gradient: true, boldtext: true}}
			withIcon="share"
			class={styles.mod({rounded: true, gradient: true}).mergeProps(props)}
		>
			<ButtonText>{children}</ButtonText>
		</ButtonCampaign>
	)
}

export const ButtonEdit: React.FC<ButtonData> = ({children, mod, ...props}) => (
	<ButtonCampaign
		mod={'greybg'}
		class={styles.mod(['rounded']).mergeProps(props)}
		withIcon="edit"
	>
		<ButtonText>{children}</ButtonText>
	</ButtonCampaign>
)
